import { Injectable } from '@angular/core';
import { Log, IShortLogger } from './logger.service';

/**
 * Screen dimensions for Guacamole connection
 */
export interface ScreenDimensions {
  width: number;
  height: number;
  dpi: number;
}

/**
 * Service for calculating optimal screen dimensions for Guacamole connections.
 *
 * Key responsibilities:
 * - Detect available viewport space accounting for browser chrome and UI elements
 * - Calculate optimal dimensions for RDP connections to maximize screen real estate
 * - Provide DPI detection for high-resolution displays
 * - Account for VM connection component UI elements (sidebar, toolbars)
 *
 * @remarks
 * RDP supports dynamic resolution adjustment, so we want to use as much screen space
 * as possible. This service calculates dimensions that:
 * 1. Fit within the browser viewport
 * 2. Account for browser chrome (address bar, tabs, bookmarks)
 * 3. Leave room for the VM connection sidebar overlay (if visible)
 * 4. Use proper DPI for retina/high-DPI displays
 *
 * @example
 * ```typescript
 * const dims = this.screenDimensionsService.getOptimalDimensions();
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class ScreenDimensionsService {
  private readonly logger: IShortLogger<ScreenDimensionsService> = Log.create(ScreenDimensionsService);

  // UI element dimensions (in pixels)
  private readonly BROWSER_CHROME_HEIGHT = 100; // Address bar, tabs, bookmarks
  private readonly SIDEBAR_WIDTH = 0; // Sidebar is overlay, doesn't reduce display area
  private readonly VERTICAL_PADDING = 20; // Some breathing room

  constructor() { }

  /**
   * Gets optimal screen dimensions for Guacamole connection.
   *
   * Calculates dimensions based on:
   * - Available viewport size (window.innerWidth/innerHeight)
   * - Browser chrome overhead
   * - Device pixel ratio for DPI calculation
   *
   * @param accountForSidebar - Whether to reduce width for sidebar (default: false, sidebar is overlay)
   * @returns Screen dimensions object with width, height, and dpi
   */
  getOptimalDimensions(accountForSidebar: boolean = false): ScreenDimensions {
    // Get available viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Calculate usable dimensions accounting for browser chrome and padding
    let usableWidth = viewportWidth;
    let usableHeight = viewportHeight - this.BROWSER_CHROME_HEIGHT - this.VERTICAL_PADDING;

    // Optionally account for sidebar (though it's typically an overlay)
    if (accountForSidebar) {
      usableWidth -= this.SIDEBAR_WIDTH;
    }

    // Ensure minimum dimensions (failsafe)
    const width = Math.max(800, Math.floor(usableWidth));
    const height = Math.max(600, Math.floor(usableHeight));

    // Calculate DPI based on device pixel ratio
    // Standard DPI is 96, scale based on devicePixelRatio
    const dpi = Math.round(96 * (window.devicePixelRatio || 1));

    const dimensions: ScreenDimensions = { width, height, dpi };

    this.logger.d(
      `Calculated optimal dimensions: ${width}x${height} @ ${dpi} DPI ` +
      `(viewport: ${viewportWidth}x${viewportHeight}, devicePixelRatio: ${window.devicePixelRatio})`
    );

    return dimensions;
  }

  /**
   * Gets dimensions for trainer joining student session (connection sharing).
   *
   * For shared connections, we might want slightly different dimensions:
   * - Typically using a larger display (trainer monitor)
   * - May want to scale up to match trainer's screen
   *
   * @returns Screen dimensions optimized for trainer view
   */
  getTrainerViewDimensions(): ScreenDimensions {
    // For trainer view, use full available space
    const dims = this.getOptimalDimensions(false);

    this.logger.d(`Trainer view dimensions: ${dims.width}x${dims.height} @ ${dims.dpi} DPI`);

    return dims;
  }

  /**
   * Gets dimensions for a specific element container.
   *
   * Useful when the Guacamole display is within a specific container element
   * rather than using the full viewport.
   *
   * @param element - The container element to measure
   * @returns Screen dimensions based on element size
   */
  getDimensionsForElement(element: HTMLElement): ScreenDimensions {
    const rect = element.getBoundingClientRect();

    // Use element dimensions minus some padding
    const width = Math.max(800, Math.floor(rect.width - 20));
    const height = Math.max(600, Math.floor(rect.height - 20));
    const dpi = Math.round(96 * (window.devicePixelRatio || 1));

    this.logger.d(`Element dimensions: ${width}x${height} @ ${dpi} DPI (element: ${rect.width}x${rect.height})`);

    return { width, height, dpi };
  }

  /**
   * Formats dimensions as query string parameters.
   *
   * Creates URL query parameters suitable for appending to WebSocket URL:
   * - width={width}
   * - height={height}
   * - dpi={dpi}
   *
   * @param dimensions - Screen dimensions to format
   * @returns Query string (without leading '?' or '&')
   */
  formatAsQueryParams(dimensions: ScreenDimensions): string {
    return `width=${dimensions.width}&height=${dimensions.height}&dpi=${dimensions.dpi}`;
  }

  /**
   * Logs current screen information for debugging.
   */
  logScreenInfo(): void {
    this.logger.d('Screen Information:', {
      'window.innerWidth': window.innerWidth,
      'window.innerHeight': window.innerHeight,
      'window.outerWidth': window.outerWidth,
      'window.outerHeight': window.outerHeight,
      'screen.width': window.screen.width,
      'screen.height': window.screen.height,
      'screen.availWidth': window.screen.availWidth,
      'screen.availHeight': window.screen.availHeight,
      'devicePixelRatio': window.devicePixelRatio,
      'optimal': this.getOptimalDimensions()
    });
  }
}
